import {
  initializeFirebaseClient,
  type FirebaseClientConfig,
} from "./firebase";

/**
 * Fallback Firebase web config for the `katanaai-pharma-dev` project. These
 * are public client identifiers (protected by Firestore/Storage security
 * rules and App Check, not by secrecy — see firebase-env.ts callers), so it
 * is safe to bake them in as a safety-net default.
 *
 * Used ONLY when a `VITE_FIREBASE_*` env var is missing at build/runtime, so
 * a misconfigured build (missing .env.local, env vars not injected in CI,
 * etc.) always falls back to the DEV project instead of failing in a way
 * that could be worked around by accidentally pointing at prod. Real
 * production deploys must set `VITE_FIREBASE_PROJECT_ID=katanaai-pharma`
 * (and siblings) explicitly to opt out of this default.
 */
export const DEV_FIREBASE_DEFAULTS: Record<string, string> = {
  VITE_FIREBASE_API_KEY: "AIzaSyD8VQ_YnnlaozG9VjB0py2-2CPn4ayDQa0",
  VITE_FIREBASE_AUTH_DOMAIN: "katanaai-pharma-dev.firebaseapp.com",
  VITE_FIREBASE_PROJECT_ID: "katanaai-pharma-dev",
  VITE_FIREBASE_STORAGE_BUCKET: "katanaai-pharma-dev.firebasestorage.app",
  VITE_FIREBASE_MESSAGING_SENDER_ID: "583647062253",
  VITE_FIREBASE_APP_ID: "1:583647062253:web:72e743890526f1094e04e4",
};

/**
 * Boot the Firebase client from `VITE_FIREBASE_*` env vars. Falls back to
 * `DEV_FIREBASE_DEFAULTS` for any missing var, so the default environment is
 * always the dev Firebase project — never a silent/undefined state. Used
 * identically by every admin panel — keeps the env-var contract in one
 * place.
 */
export function bootstrapFirebaseFromEnv(
  env: Record<string, string | undefined>,
  artifactPath: string,
): void {
  function read(name: string): string {
    const v = env[name];
    if (v && v.length > 0) return v;
    const fallback = DEV_FIREBASE_DEFAULTS[name];
    if (fallback) {
      console.warn(
        `[firebase-env] ${name} not set in ${artifactPath}/.env.local or deployment env — defaulting to the dev Firebase project.`,
      );
      return fallback;
    }
    throw new Error(
      `Missing required env var ${name}. Set it in ${artifactPath}/.env.local or in deployment env.`,
    );
  }
  const config: FirebaseClientConfig = {
    apiKey: read("VITE_FIREBASE_API_KEY"),
    authDomain: read("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: read("VITE_FIREBASE_PROJECT_ID"),
    storageBucket: read("VITE_FIREBASE_STORAGE_BUCKET"),
    messagingSenderId: read("VITE_FIREBASE_MESSAGING_SENDER_ID"),
    appId: read("VITE_FIREBASE_APP_ID"),
    // App Check is OPTIONAL — absent key = App Check off, client unchanged.
    appCheckSiteKey: env.VITE_APPCHECK_RECAPTCHA_SITE_KEY,
    appCheckDebugToken: env.VITE_APPCHECK_DEBUG_TOKEN,
  };
  initializeFirebaseClient(config, "europe-west1");
}
